// src/config/homeConfig.ts
// Content source of truth for the Home page.
//
// Home is deliberately thin: who this is, what he builds, proof of
// experience, and one clear path to contact. The deeper professional
// narrative — domains, engineering approach, capabilities, journey — lives
// in aboutConfig.ts and is linked to, not restated here.
//
// Sections are added to this file as they are implemented.

export interface HomeCtaAction {
  label: string;
  to: string;
}

/**
 * One layer of the system the hero diagram describes. These name the parts
 * of a backend system, in the order they appear in a request's path.
 */
export interface HomeHeroLayer {
  name: string;
  detail: string;
}

/**
 * One label/value row of the experience snapshot. Deliberately flat text
 * rather than a stat object: this is a profile summary, not a dashboard.
 */
export interface HomeSnapshotRow {
  /** Monospace uppercase label. */
  label: string;
  /** The answer. A phrase, not a number with a unit. */
  value: string;
}

/**
 * One engineering capability. `stack` is restricted to technologies that are
 * evidenced in the resume, mirroring the `documented` flag used by
 * aboutConfig.capabilities - this is a claim about contribution, so
 * unevidenced items stay off the page.
 */
export interface HomeCapabilityItem {
  id: string;
  index: string;
  title: string;
  /**
   * Marks the primary specialism. Exactly one item sets this, so the page
   * never implies six equal specialisms.
   */
  primary?: boolean;
  /** One sentence on what this capability is responsible for. */
  summary: string;
  /** What the work actually involves. */
  focus: string[];
  /** Documented technology context. */
  stack: string[];
}

/** Icon name, mapped to a react-icons component in the component file. */
export interface HomeProjectLinks {
  github?: string;
  demo?: string;
}

/**
 * A personal project. Distinct from the professional systems in
 * `highlights` and from the client engagements on /about - this is for work
 * that can be shown publicly.
 *
 * There is deliberately no `stats`, `role` or `outcome` field: the section
 * must not be able to express a metric that has not been supplied.
 */
export interface HomeProject {
  id: string;
  name: string;
  /** One-line summary, shown in the supporting row. */
  summary: string;
  /** Longer description, shown in the featured slot. */
  description?: string;
  /** The problem or context the project was built for. */
  context?: string;
  technologies: string[];
  /** e.g. 'Web application', 'Backend service', 'Internal tool'. */
  type: string;
  /** Path to a screenshot, or a key for a rendered placeholder visual. */
  visual?: string;
  links?: HomeProjectLinks;
  category?: string;
  /** Promoted to the large slot. Keep to one. */
  featured?: boolean;
}

export interface HomeWorkEmptyState {
  /** Mono badge marking the state as temporary. */
  badge: string;
  title: string;
  body: string;
}

export interface HomeWorkSection {
  eyebrow: string;
  heading: string;
  lede: string;
  /**
   * Empty until real project data is supplied. siteConfig.projects is
   * placeholder text and is deliberately not read here.
   */
  projects: HomeProject[];
  empty: HomeWorkEmptyState;
  cta: HomeCtaAction;
  /** Label above the supporting row. */
  moreLabel: string;
  /** Label on the per-project link. */
  viewLabel: string;
}

export type HomeHighlightIcon = 'aviation' | 'iot' | 'asset' | 'energy';

/**
 * One real system he has worked on. Deliberately not a project entry: there
 * are no project names, dates or links, because the resume documents the
 * position rather than the individual engagements.
 */
export interface HomeHighlightItem {
  id: string;
  index: string;
  icon: HomeHighlightIcon;
  /** Full domain name. */
  domain: string;
  /** Short label for the node. */
  shortLabel: string;
  /** Client context, where the resume names one. */
  client?: string;
  /** One sentence on the system. */
  description: string;
  /** One or two engineering focus areas. */
  focus: string[];
}

export interface HomeHighlightsSection {
  eyebrow: string;
  heading: string;
  lede: string;
  /** Label above the node row. */
  listLabel: string;
  focusLabel: string;
  items: HomeHighlightItem[];
  /** Closing note tying the four systems together. */
  stackNote: string;
}

export interface HomeCapabilitiesSection {
  eyebrow: string;
  heading: string;
  lede: string;
  /** Tag shown against the primary capability. */
  primaryLabel: string;
  listLabel: string;
  focusLabel: string;
  stackLabel: string;
  items: HomeCapabilityItem[];
}

export interface HomeSnapshotSection {
  eyebrow: string;
  heading: string;
  /** One or two sentences on the shape of the career so far. */
  statement: string;
  /** Heading for the right-hand spec sheet. */
  snapshotLabel: string;
  rows: HomeSnapshotRow[];
  /** Named domains, shown as evidence beneath the count. */
  domains: string[];
  cta: HomeCtaAction;
}

export interface HomeHeroSection {
  /** Short uppercase line above the name that sets the specialism. */
  eyebrow: string;
  /** Professional title, stated once and not varied. */
  role: string;
  /** One or two sentences on what is actually built. */
  supporting: string;
  /** Years of professional experience, with the context they were spent in. */
  experience: {
    value: string;
    label: string;
  };
  /** Scannable capability line. Kept to single words — no technology lists. */
  capabilityLine: string[];
  /** Label for the system-layer diagram. */
  layersLabel: string;
  layers: HomeHeroLayer[];
  /**
   * A single primary action. This was three CTAs, and every one of them
   * duplicated a link further down the page: /projects was also offered by the
   * work section, /about by the snapshot, and /get-in-touch by the closing
   * CTA. The hero states the positioning; the conversion action belongs to
   * the top and the bottom of the page, not three times in the first screen.
   */
  cta: HomeCtaAction;
}

export const homeConfig: {
  hero: HomeHeroSection;
  snapshot: HomeSnapshotSection;
  capabilities: HomeCapabilitiesSection;
  highlights: HomeHighlightsSection;
  work: HomeWorkSection;
} = {
  hero: {
    eyebrow: 'Enterprise Backend Engineering',
    role: 'Backend Engineer — Enterprise Application Development',
    supporting:
      'I build backend services, REST APIs and system integrations for enterprise applications — written in C# / .NET, and connected to the front-ends that consume them.',
    experience: {
      value: '4.5+ years',
      label:
        'of professional software development across aviation operations, smart IoT, IT/OT asset management, and energy and resources data.',
    },
    capabilityLine: ['.NET', 'APIs', 'React', 'Databases', 'Cloud'],
    layersLabel: 'System layers',
    layers: [
      { name: 'Backend services', detail: 'C# / .NET / .NET Core' },
      { name: 'REST APIs', detail: 'ASP.NET Core' },
      { name: 'Integrations', detail: 'Event-driven messaging and background jobs' },
      { name: 'Data', detail: 'Relational and document stores' },
    ],
    // The one primary action. Deliberately the enquiry route rather than
    // /projects: the work section below is a placeholder, so opening on
    // "view my work" would lead with the emptiest part of the page.
    cta: { label: "Let's Discuss the Project", to: '/get-in-touch' },
  },

  // The concise professional profile. The hero states the role; this section
  // states the shape of the experience and hands off to /about for depth.
  //
  // This previously carried a second paragraph explaining that the work spans
  // APIs, front-ends, data stores and messaging. That restated the capabilities
  // section almost line for line, so it is gone: the spec sheet and the domain
  // list carry the section on their own.
  snapshot: {
    eyebrow: 'Professional background',
    heading: 'The shape of the work',
    statement:
      'Four and a half years in one engineering role, across four client engagements rather than a series of short projects.',
    snapshotLabel: 'Experience snapshot',
    rows: [
      { label: 'Experience', value: '4.5+ years' },
      { label: 'Primary focus', value: 'Backend engineering' },
      { label: 'Builds', value: 'Enterprise applications' },
      { label: 'Core stack', value: 'C# / .NET / .NET Core' },
    ],
    domains: [
      'Aviation & trip operations',
      'Smart IoT & appliances',
      'IT/OT asset & ISMS',
      'Energy & resources data',
    ],
    cta: { label: 'Read My Experience', to: '/about' },
  },

  // What he can be responsible for on a project. Backend is flagged as the
  // primary specialism so the list is not read as six equal offerings.
  capabilities: {
    eyebrow: 'Engineering capabilities',
    heading: 'What I can own on a project',
    lede: 'Most of my work is backend. These are the parts of a system I can be responsible for, listed by what each one does rather than as six equal specialisms.',
    primaryLabel: 'Primary focus',
    listLabel: 'Engineering capabilities',
    focusLabel: 'In practice',
    stackLabel: 'Documented technology',
    items: [
      {
        id: 'backend-api',
        index: '01',
        title: 'Backend & API Engineering',
        primary: true,
        summary:
          'Designing and implementing REST APIs and backend services, and keeping them maintainable as the requirements around them change.',
        focus: [
          'REST API design',
          'Service boundaries',
          'Request and response contracts',
          'Validation and error handling',
        ],
        stack: ['C#', '.NET', '.NET Core', 'ASP.NET Core', 'REST APIs'],
      },
      {
        id: 'enterprise-apps',
        index: '02',
        title: 'Enterprise Application Development',
        summary:
          'Translating real business workflows and domain requirements into application functionality that holds up outside a demo.',
        focus: [
          'Business requirement analysis',
          'Domain modelling',
          'Workflow and state handling',
          'Test coverage alongside delivery',
        ],
        stack: ['C#', '.NET', 'TDD', 'Code Reviews'],
      },
      {
        id: 'integration',
        index: '03',
        title: 'System Integration',
        summary:
          'Connecting applications to external platforms, services, messaging systems and data sources that were not designed to talk to each other.',
        focus: [
          'External API integration',
          'Event-driven messaging',
          'Data synchronisation',
          'Retry and failure handling',
        ],
        stack: ['Azure Service Bus', 'REST APIs', 'Outbox Messaging Pattern'],
      },
      {
        id: 'data-processing',
        index: '04',
        title: 'Data & Backend Processing',
        summary:
          'Working with relational and document data, and moving the work that does not belong on the request thread into background processing.',
        focus: [
          'Data access layers',
          'Relational and NoSQL modelling',
          'Background and scheduled jobs',
          'Keeping stores consistent',
        ],
        stack: [
          'PostgreSQL',
          'SQL Server',
          'Oracle',
          'MongoDB',
          'Hangfire',
          'Background Jobs',
        ],
      },
      {
        id: 'architecture',
        index: '05',
        title: 'Application Architecture',
        summary:
          'Applying architecture patterns where the problem calls for them, so the codebase can absorb change without needing a rewrite.',
        focus: [
          'Separation of concerns',
          'Dependency inversion',
          'Read and write separation',
          'Testability of the core',
        ],
        stack: [
          'Clean Architecture',
          'CQRS',
          'Repository Pattern',
          'OOP / SOLID',
          'Event-Driven Architecture',
        ],
      },
      {
        id: 'frontend-integration',
        index: '06',
        title: 'Frontend Integration',
        summary:
          'Enough front-end to build against the API contract and integrate alongside the backend, not to lead the front-end.',
        focus: [
          'API contract definition',
          'Shared data models',
          'TypeScript types from the API',
          'Integration alongside the service',
        ],
        stack: ['React', 'React TypeScript'],
      },
    ],
  },

  // Visual proof of the systems worked on. Not a project portfolio: no
  // project names, dates or links, because the resume documents the position
  // rather than the individual engagements. The full account is on /about,
  // which this section's lede points to - it has no button of its own, because
  // the snapshot above already carries the page's one hard /about CTA.
  highlights: {
    eyebrow: 'Enterprise systems',
    heading: 'Experience across real systems',
    lede: 'Systems in use at client organisations rather than sample projects. The full account of each is on the About page.',
    listLabel: 'Business domains',
    focusLabel: 'Engineering focus',
    items: [
      {
        id: 'aviation',
        index: '01',
        icon: 'aviation',
        domain: 'Aviation & Trip Operations',
        shortLabel: 'Aviation',
        client: 'Universal Weather and Aviation',
        description:
          'Trip and operations management for an aviation operator, covering planning, tracking and reporting across trips and the operational activity around them.',
        focus: [
          'Trip and scheduling rules kept independent of persistence and transport',
          'REST APIs structured with Clean Architecture and CQRS',
        ],
      },
      {
        id: 'iot',
        index: '02',
        icon: 'iot',
        domain: 'Smart IoT Home & Appliance Platform',
        shortLabel: 'Smart IoT',
        client: 'Tata Power',
        description:
          'A connected home and appliance platform where device state has to stay consistent between the device, the platform and the person using it.',
        focus: [
          'Device onboarding and device state management',
          'Event-driven messaging between device-facing and application services',
        ],
      },
      {
        id: 'asset',
        index: '03',
        icon: 'asset',
        domain: 'IT/OT Asset Management & ISMS',
        shortLabel: 'IT/OT & ISMS',
        client: 'Deloitte India',
        description:
          'Asset lifecycle tracking and information security management for organisations running both IT and OT estates, where records and compliance evidence have to stay consistent.',
        focus: [
          'Asset records and ISMS compliance tracking kept in step',
          'Repository abstraction between domain and persistence',
        ],
      },
      {
        id: 'energy',
        index: '04',
        icon: 'energy',
        domain: 'Energy & Resources Data Collection',
        shortLabel: 'Energy & Resources',
        client: 'SMRD Tata Power',
        description:
          'Collection of field data across energy and resources operations, processed into something operations could act on and report against.',
        focus: [
          'Scheduled and background processing into operational stores',
          'Relational data modelling for operational reporting',
        ],
      },
    ],
    // The leading phrase is rendered as <strong> by the component, so this
    // value is the list only. It previously repeated that phrase, printing
    // "Applied across all four systems" twice in a row.
    stackNote:
      'C# / .NET / .NET Core, ASP.NET Core REST APIs, Azure Service Bus, Hangfire background jobs, PostgreSQL / SQL Server / Oracle / MongoDB, and React (TypeScript) for frontend integration.',
  },

  // Personal projects. Intentionally empty.
  //
  // siteConfig.projects is NOT used here: it contains placeholder entries
  // (a "TimeCraft" scheduling app, a "MERN Student Routine Tracker", and this
  // portfolio itself) that are not supported by the resume and describe a
  // MERN/Node stack that contradicts the documented .NET background. It is
  // still rendered by src/pages/Projects.tsx and should be replaced there too.
  //
  // To publish work, append objects matching HomeProject. The section picks
  // one featured project for the large slot and renders the rest as the
  // supporting row; no other change is needed.
  work: {
    eyebrow: 'Selected work',
    heading: 'Projects and build work',
    lede: 'Personal projects, side work and tooling.',
    projects: [],
    // The lede, the title and the body must not all restate the same fact.
    // The lede says what belongs here, the title states the position, and the
    // body says where the professional work actually is.
    empty: {
      badge: 'Awaiting project data',
      title: 'No projects published here yet',
      body: 'Project write-ups are still being prepared. The professional work is documented on the About page, and View My Work at the end of this page covers the project index.',
    },
    moreLabel: 'More work',
    viewLabel: 'View Project',
    // Rendered only once projects.length > 0. While the section is empty it
    // would be a third link to /projects on the page, immediately above the
    // closing CTA's "View My Work" which goes to the same route.
    cta: { label: 'View All Projects', to: '/projects' },
  },
};
